import * as React from 'react';
import cx from 'classnames';
import px from 'prop-types';
import { useViewport } from 'Common/hooks';
import { Product } from 'Common/types';
import { Url, GTM as gtmUtil } from 'Common/utils';
import * as GTM from 'Common/constants/gtm';
import * as PRODUCT from '~config/product';
import { getLinkRel } from 'Common/utils/LinkAccessibility';
import { Translation } from '../localization';
import { Loader } from '../ui';
import { useLinkLabel } from '../ui/LinkHint';
import ProductPrice from './ProductPrice';

/**
 * Extracted from the map below so the accessible-name hook can run once per product -- hooks cannot
 * be called inside a loop. The anchor is named by aria-label, which would suppress any hidden text
 * nested inside it, so the behaviour hint is folded into that label.
 */
function RelatedProduct({ product, onClick }) {
    const isSameSite = new URL(product?.ContentUrl).host === location.host;
    const target = isSameSite ? '_self' : '_blank';
    const ariaLabel = useLinkLabel(product.DisplayName, product?.ContentUrl, target);

    return (
        <a
            href={product?.ContentUrl}
            target={target}
            rel={getLinkRel(target) || undefined}
            onClick={onClick}
            aria-label={ariaLabel}
        >
            <img
                className="RelatedProducts__product__image fit-image-main"
                src={Url.addToRelativeUrl(
                    product.DefaultImageUrl,
                    `format=pngheight=175&width=250${PRODUCT.TRANSFORM_PRODUCT_IMAGE_BACKGROUND ? '&transBg=true' : ''}`
                )}
                alt={product.DisplayName}
            />
            <div className="subheader">{product.DisplayName}</div>
            {product.Price ? (
                <ProductPrice priceInfo={{ price: product.Price, useQtySalePrice: false }} quantity={1} />
            ) : null}
        </a>
    );
}

RelatedProduct.propTypes = {
    product: Product,
    onClick: px.func,
};

export default function RelatedProducts({ className, relatedProducts }) {
    const viewport = useViewport();
    const gtmImpression = React.useRef(false);
    const showProducts = React.useMemo(
        () => !viewport.is.mobile && relatedProducts?.length,
        [viewport, relatedProducts]
    );

    const onSelectProduct = React.useCallback(
        (product, index, onClick) => () => {
            gtmUtil.updateDataLayer(gtmUtil.mapEntityToImpClick(product, GTM.TAGS.RELATED));
            onClick();
        },
        []
    );

    React.useEffect(() => {
        if (!gtmImpression.current && showProducts && relatedProducts?.length) {
            gtmUtil.updateDataLayer(gtmUtil.mapEntityToImpressions(relatedProducts, GTM.TAGS.RELATED));
            gtmImpression.current = true;
        }
    }, [relatedProducts, showProducts]);

    return (
        <div className={cx('RelatedProducts text-center', className)}>
            <div className="RelatedProducts__title">
                <Translation id="Commerce.Product.RelatedProducts.Title" />
            </div>
            {relatedProducts?.length ? (
                relatedProducts.map((product, index) => (
                    //TODO: EDS-3143
                    <div key={product.Code} className="RelatedProducts__product mt-4">
                        <RelatedProduct product={product} onClick={onSelectProduct(product, index)} />
                    </div>
                ))
            ) : relatedProducts ? null : (
                <div className="d-flex justify-content-center align-items-center" style={{ height: '100%' }}>
                    <Loader noOverlay />
                </div>
            )}
        </div>
    );
}

RelatedProducts.propTypes = {
    className: px.string,
    relatedProducts: px.arrayOf(Product),
};
